import React, { useState, useRef, useEffect } from 'react';

// A curated palette rather than the browser's native color dialog — that
// dialog's own "basic colors" grid can't be trimmed or restyled, so a
// small custom popover (presets + a hex field for anything else) is both
// simpler to use and fully within our own visual control.
const PRESET_COLORS = [
  '#111111', '#ffffff', '#8c8c8c',
  '#c0392b', '#e67e22', '#f1c40f',
  '#2ecc71', '#1abc9c', '#3498db',
  '#8e44ad', '#e84393',
];

// Matches the popover's actual rendered width (6 preset columns + gaps +
// padding) closely enough to keep the clamp below from over- or
// under-correcting near a viewport edge.
const POPOVER_WIDTH = 185;
const VIEWPORT_MARGIN = 8;

function ColorField({ label, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  const wrapperRef = useRef(null);
  const swatchRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    // The trigger lives inside a scrollable panel (which may be quite
    // short once its height is matched to the survey card) — a plain
    // CSS-anchored popover gets clipped by that container's overflow, so
    // position is computed in JS and rendered fixed to the viewport
    // instead. Closing on scroll keeps it from drifting out of alignment.
    const handleScroll = () => setIsOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const toggleOpen = () => {
    if (!isOpen && swatchRef.current) {
      const rect = swatchRef.current.getBoundingClientRect();
      const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft));
      setPopoverPos({ top: rect.bottom + 6, left });
    }
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="color-field" ref={wrapperRef}>
      <span className="color-field-label">{label}</span>
      <span className="color-field-control">
        <button
          ref={swatchRef}
          type="button"
          className="color-field-swatch"
          style={{ backgroundColor: value }}
          onClick={toggleOpen}
          aria-label={`${label} color, currently ${value}`}
          aria-expanded={isOpen}
        />
        <input
          type="text"
          className="color-field-hex"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          maxLength={7}
        />
        {isOpen && popoverPos && (
          <div className="color-field-popover" style={{ top: popoverPos.top, left: popoverPos.left }}>
            <div className="color-field-presets">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className="color-field-preset"
                  style={{ backgroundColor: preset }}
                  onClick={() => {
                    onChange(preset);
                    setIsOpen(false);
                  }}
                  aria-label={preset}
                />
              ))}
            </div>
          </div>
        )}
      </span>
    </div>
  );
}

export default ColorField;
